import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  Collapse,
  Drawer,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Alert,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorSharpIcon from "@mui/icons-material/ErrorSharp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import GroupedSelect from "../../../utils/GroupedSelect";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import { YearPicker } from "../../../utils/Form/YearPicker";
import { TextFieldForViewOnly } from "../../../utils/TextField/TextField";
import {
  EXTERNAL_TYPES,
  FIELD_LABELS,
  displayValue,
  hasInitialAllowance,
  isAssetExpensedOffCategory,
  isApplicableType,
  isEnhancedTwdvType,
  isFixedClaimYears,
  isPLScheduleAsset,
} from "./rules";
import { formatAmount } from "../../../../utils/hooks/dataHandler";

const numericFields = new Set([
  "costOfAsset",
  "yaOfPurchase",
  "noOfYearsClaim",
  "noOfYearsBf",
  "twdvBf",
  "enhancedTwdvBf",
]);

const requiredFields = new Set([
  "assetCategory",
  "costOfAsset",
  "yaOfPurchase",
  "caType",
]);

const calculatedDrawerFields = new Set([
  "noOfYearsCf",
  "iaRate",
  "ia",
  "aa",
  "twdvCf",
]);

const conditionalDrawerFields = new Set(["enhancedTwdvBf", "iaRate", "ia"]);
const applicableOnlyDrawerFields = new Set([
  "noOfYearsClaim",
  "deferred",
  "disposal",
  "twdvBf",
  "noOfYearsBf",
  "noOfYearsCf",
  "aa",
  "twdvCf",
]);
const hideForHirePurchaseFields = new Set(["deferred", "disposal"]);
const booleanDrawerFields = new Set(["deferred", "disposal"]);

const isBlankValue = (value) =>
  value === null || value === undefined || value === "";

const headerGroups = [
  {
    label: "Asset Details",
    fields: [
      "assetCategory",
      "assetDescription",
      "caType",
      "yaOfPurchase",
      "costOfAsset",
      "noOfYearsClaim",
      "deferred",
      "disposal",
    ],
  },
  {
    label: "Current YA & Closing",
    fields: [
      "twdvBf",
      "enhancedTwdvBf",
      "noOfYearsBf",
      "noOfYearsCf",
      "iaRate",
      "ia",
      "aa",
      "twdvCf",
    ],
  },
];

const FaRegisterDrawer = ({
  open,
  rows,
  activeAsset,
  activeAssetIndex,
  visibleFields,
  validations,
  saveAttempted,
  currentYa,
  caSettings,
  categoryOptions = [],
  caTypeSelectOptions = [],
  canEditSchedule,
  onClose,
  onInputChange,
  onQuickAddCategory,
  onNavigateAsset,
  onDuplicateAsset,
  onDeleteAsset,
}) => {
  const theme = useTheme();
  const twdvBfRowRef = useRef(null);
  const [expandedGroups, setExpandedGroups] = useState(
    () => new Set(["Asset Details"])
  );
  const isPriorYaAsset =
    !!activeAsset?.yaOfPurchase &&
    Number(activeAsset.yaOfPurchase) !== Number(currentYa);
  const needsClosingInput =
    isPriorYaAsset &&
    (isBlankValue(activeAsset?.twdvCf) ||
      isBlankValue(activeAsset?.noOfYearsCf));

  useEffect(() => {
    if (open) {
      const nextGroups = new Set(["Asset Details"]);
      if (isPriorYaAsset) nextGroups.add("Current YA & Closing");
      setExpandedGroups(nextGroups);
    }
  }, [open, isPriorYaAsset]);

  useEffect(() => {
    if (!open || !needsClosingInput) return undefined;

    const scrollTimer = window.setTimeout(() => {
      twdvBfRowRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 250);

    return () => window.clearTimeout(scrollTimer);
  }, [
    open,
    needsClosingInput,
    activeAsset?.rowKey,
    activeAsset?.yaOfPurchase,
    activeAsset?.twdvCf,
    activeAsset?.noOfYearsCf,
  ]);

  useEffect(() => {
    if (!open) return undefined;

    const handleDrawerKeyDown = (event) => {
      if (!event.altKey || event.defaultPrevented) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onNavigateAsset(-1);
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        onNavigateAsset(1);
      }
    };

    window.addEventListener("keydown", handleDrawerKeyDown);
    return () => window.removeEventListener("keydown", handleDrawerKeyDown);
  }, [open, onNavigateAsset]);

  const toggleDrawerGroup = (label) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const renderError = (rowKey, field) => {
    const message = validations[rowKey]?.errors[field];
    if (!message || !saveAttempted) return null;

    return (
      <Tooltip title={message}>
        <ErrorSharpIcon color='error' fontSize='small' sx={{ ml: 0.5 }} />
      </Tooltip>
    );
  };

  const renderTextInput = (row, field, options = {}) => {
    const {
      disabled = false,
      width = 150,
      align = "left",
      multiline = false,
      minRows = 1,
    } = options;
    const error = !!(saveAttempted && validations[row.rowKey]?.errors[field]);

    if (!canEditSchedule) {
      return (
        <TextFieldForViewOnly
          value={
            numericFields.has(field) ? formatAmount(row[field]) : row[field]
          }
          sx={{ textAlign: align }}
        />
      );
    }

    return (
      <Stack direction='row' alignItems='center' spacing={0.25} sx={{ width }}>
        <TextField
          value={row[field] ?? ""}
          onChange={(event) =>
            onInputChange(row.rowKey, field, event.target.value)
          }
          size='small'
          disabled={disabled}
          error={error}
          multiline={multiline}
          minRows={minRows}
          inputProps={{
            inputMode: numericFields.has(field) ? "numeric" : undefined,
            style: { textAlign: align },
          }}
          sx={{ width }}
        />
        {renderError(row.rowKey, field)}
      </Stack>
    );
  };

  const renderBooleanInput = (row, field, disabled = false) => {
    if (disabled) return <TextFieldForViewOnly value='N.A.' />;

    if (!canEditSchedule) {
      return <TextFieldForViewOnly value={row[field] ? "Yes" : "No"} />;
    }

    return (
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-end'
        spacing={0.75}
        sx={{ width: "100%" }}
      >
        {renderError(row.rowKey, field)}
        <Typography variant='caption' color='text.secondary'>
          {row[field] ? "Yes" : "No"}
        </Typography>
        <Checkbox
          checked={!!row[field]}
          onChange={(event) =>
            onInputChange(row.rowKey, field, event.target.checked)
          }
          disabled={disabled}
          size='small'
        />
      </Stack>
    );
  };

  const renderCaTypeInput = (row, options = {}) => {
    const { width = 190 } = options;
    const error = !!(saveAttempted && validations[row.rowKey]?.errors.caType);

    if (!canEditSchedule) return <TextFieldForViewOnly value={row.caType} />;

    return (
      <Stack direction='row' alignItems='center' spacing={0.25} sx={{ width }}>
        <Box sx={{ width }}>
          <GroupedSelect
            value={row.caType || ""}
            onChange={(event) =>
              onInputChange(row.rowKey, "caType", event.target.value)
            }
            options={caTypeSelectOptions}
            placeholder='Select CA Type'
            enableSearch
            canEdit={canEditSchedule}
            sx={{
              width: "100%",
              ...(error
                ? {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "error.main",
                    },
                  }
                : {}),
            }}
            menuProps={{
              sx: { zIndex: (theme) => theme.zIndex.modal + 4 },
              PaperProps: {
                sx: { zIndex: (theme) => theme.zIndex.modal + 5 },
              },
            }}
          />
        </Box>
        {renderError(row.rowKey, "caType")}
      </Stack>
    );
  };

  const renderCategoryInput = (row, options = {}) => {
    const { width = "100%" } = options;
    const categorySelectOptions = EXTERNAL_TYPES.has(row.caType)
      ? categoryOptions.filter(
          (option) =>
            !isAssetExpensedOffCategory(option.value) &&
            !isAssetExpensedOffCategory(option.label)
        )
      : categoryOptions;
    const error = !!(
      saveAttempted && validations[row.rowKey]?.errors.assetCategory
    );

    if (!canEditSchedule) {
      return <TextFieldForViewOnly value={row.assetCategory || ""} />;
    }

    return (
      <Stack direction='row' alignItems='center' spacing={0.25} sx={{ width }}>
        <Box sx={{ width }}>
          <GroupedSelect
            value={row.assetCategoryId || ""}
            onChange={(event) =>
              onInputChange(row.rowKey, "assetCategory", event.target.value)
            }
            options={categorySelectOptions}
            placeholder={
              categorySelectOptions.length
                ? "Select Asset Category"
                : "Type to add a category"
            }
            searchPlaceholder='Add or search category...'
            enableSearch
            canEdit={canEditSchedule}
            onCreateOption={
              onQuickAddCategory
                ? (name) => onQuickAddCategory(name, row.rowKey)
                : undefined
            }
            createOptionLabel={(name) => `Add category "${name}"`}
            sx={{
              width: "100%",
              ...(error
                ? {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "error.main",
                    },
                  }
                : {}),
            }}
            menuProps={{
              sx: { zIndex: (theme) => theme.zIndex.modal + 4 },
              PaperProps: {
                sx: { zIndex: (theme) => theme.zIndex.modal + 5 },
              },
            }}
          />
        </Box>
        {renderError(row.rowKey, "assetCategory")}
      </Stack>
    );
  };

  const renderYaOfPurchaseInput = (row) => {
    const error = renderError(row.rowKey, "yaOfPurchase");
    const maxYaOfPurchase = isPLScheduleAsset(row)
      ? Number(currentYa) - 1
      : currentYa;

    if (!canEditSchedule) {
      return <TextFieldForViewOnly value={row.yaOfPurchase || ""} />;
    }

    return (
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-end'
        spacing={0.25}
        sx={{ width: "100%" }}
      >
        <YearPicker
          year={row.yaOfPurchase || ""}
          onChange={(year) =>
            onInputChange(row.rowKey, "yaOfPurchase", year || "")
          }
          customMaxDate={new Date(new Date().setFullYear(maxYaOfPurchase))}
          allowManualInput
          canEdit={canEditSchedule}
          sx={{ width: 335, textAlign: "right" }}
          showAdornments={true}
        />
        {error}
      </Stack>
    );
  };

  const renderReadOnlyValue = (row, field, forceNA = false) => {
    const value = forceNA ? "N.A." : displayValue(row, field);
    const error = renderError(row.rowKey, field);

    return (
      <Stack direction='row' alignItems='center' justifyContent='flex-end'>
        <TextFieldForViewOnly
          value={value}
          sx={{
            width: 300,
            textAlign: "right",
            color: value === "Disposed" ? "warning.main" : undefined,
            fontWeight: value === "Disposed" ? 600 : undefined,
          }}
        />
        {error}
      </Stack>
    );
  };

  const renderFieldControl = (row, field) => {
    const applicable = isApplicableType(row.caType);
    const isHirePurchase = row.caType === "Hire Purchase";
    const currentAsset = Number(row.yaOfPurchase) === currentYa;
    const fixedClaim = isFixedClaimYears(row.caType, caSettings);

    switch (field) {
      case "assetCategory":
        return renderCategoryInput(row, { width: "100%" });
      case "assetDescription":
        return renderTextInput(row, field, {
          width: "100%",
          multiline: true,
          minRows: 3,
        });
      case "costOfAsset":
        return renderTextInput(row, field, {
          width: "100%",
          align: "right",
        });
      case "twdvBf":
        return applicable ? (
          renderTextInput(row, field, {
            width: "100%",
            align: "right",
            disabled: currentAsset,
          })
        ) : (
          <TextFieldForViewOnly value='N.A.' />
        );
      case "enhancedTwdvBf":
        return isEnhancedTwdvType(row.caType, caSettings) ? (
          renderTextInput(row, field, { width: "100%", align: "right" })
        ) : (
          <TextFieldForViewOnly value='N.A.' />
        );
      case "yaOfPurchase":
        return renderYaOfPurchaseInput(row);
      case "caType":
        return renderCaTypeInput(row, { width: "100%" });
      case "disposal":
        return renderBooleanInput(row, field, isHirePurchase);
      case "deferred":
        return renderBooleanInput(row, field, !applicable || isHirePurchase);
      case "noOfYearsClaim":
        return applicable ? (
          renderTextInput(row, field, {
            width: "100%",
            align: "center",
            disabled: fixedClaim,
          })
        ) : (
          <TextFieldForViewOnly value='N.A.' />
        );
      case "noOfYearsBf":
        return applicable ? (
          renderTextInput(row, field, {
            width: "100%",
            align: "center",
            disabled: currentAsset,
          })
        ) : (
          <TextFieldForViewOnly value='N.A.' />
        );
      case "noOfYearsCf":
        return renderReadOnlyValue(row, field);
      case "iaRate":
      case "ia":
        return renderReadOnlyValue(
          row,
          field,
          !hasInitialAllowance(row.caType, caSettings)
        );
      case "aa":
      case "twdvCf":
        return renderReadOnlyValue(row, field);
      default:
        return renderTextInput(row, field, { width: "100%" });
    }
  };

  const isDrawerFieldVisible = (row, field) => {
    const applicable = isApplicableType(row.caType);
    const isHirePurchase = row.caType === "Hire Purchase";

    if (isHirePurchase && hideForHirePurchaseFields.has(field)) return false;
    if (!applicable && applicableOnlyDrawerFields.has(field)) return false;

    if (!conditionalDrawerFields.has(field)) return true;
    if (field === "enhancedTwdvBf") {
      return isEnhancedTwdvType(row.caType, caSettings);
    }
    if (field === "iaRate" || field === "ia") {
      return hasInitialAllowance(row.caType, caSettings);
    }
    return true;
  };

  const renderDrawerField = (row, field, isLast = false) => {
    const errorMessage = saveAttempted
      ? validations[row.rowKey]?.errors[field]
      : "";
    const autoCalculated = calculatedDrawerFields.has(field);
    const isBooleanField = booleanDrawerFields.has(field);
    const isDescriptionField = field === "assetDescription";
    const showPriorYaHelper =
      field === "yaOfPurchase" &&
      !!row.yaOfPurchase &&
      Number(row.yaOfPurchase) !== Number(currentYa) &&
      isDrawerFieldVisible(row, "twdvBf");

    return (
      <Collapse
        key={field}
        in={isDrawerFieldVisible(row, field)}
        timeout='auto'
        unmountOnExit
      >
        <Box
          ref={field === "twdvBf" ? twdvBfRowRef : undefined}
          className='drawer-field-row'
          sx={{
            display: { xs: "block", sm: "grid" },
            gridTemplateColumns: "190px minmax(0, 1fr)",
            alignItems: isDescriptionField ? "flex-start" : "center",
            gap: { xs: 0.75, sm: 2 },
            px: 2,
            py: isDescriptionField ? 1.75 : 1.25,
            borderBottom: isLast ? 0 : "1px solid",
            borderColor: "divider",
            bgcolor: autoCalculated
              ? alpha(theme.palette.action.hover, 0.1)
              : "transparent",
          }}
        >
          <Box sx={{ minWidth: 0, pt: isDescriptionField ? 0.75 : 0 }}>
            <Typography
              variant='caption'
              sx={{
                display: "block",
                color: "text.secondary",
                fontWeight: 700,
                letterSpacing: 0,
              }}
            >
              {FIELD_LABELS[field]}
              {requiredFields.has(field) && (
                <Typography component='span' color='error.main'>
                  {" "}
                  *
                </Typography>
              )}
            </Typography>
          </Box>

          <Stack
            className='drawer-control'
            spacing={0.5}
            sx={{
              minWidth: 0,
              alignItems: isBooleanField ? "flex-end" : "stretch",
              "& .MuiTypography-root": autoCalculated
                ? { fontStyle: "italic" }
                : undefined,
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: isBooleanField ? "flex-end" : "stretch",
              }}
            >
              {renderFieldControl(row, field)}
            </Box>

            {errorMessage && (
              <Typography
                variant='caption'
                color='error.main'
                sx={{ display: "block", fontStyle: "normal !important" }}
              >
                {errorMessage}
              </Typography>
            )}
            {showPriorYaHelper && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: "block", fontStyle: "normal !important" }}
              >
                For prior YA assets, enter the previous YA's closing TWDV and
                years manually.
              </Typography>
            )}
          </Stack>
        </Box>
      </Collapse>
    );
  };

  const renderDrawerSectionFields = (row, fields) => {
    const visibleSectionFields = fields.filter((field) =>
      isDrawerFieldVisible(row, field)
    );

    if (!visibleSectionFields.length) return null;

    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {visibleSectionFields.map((field, index) =>
          renderDrawerField(
            row,
            field,
            index === visibleSectionFields.length - 1
          )
        )}
      </Box>
    );
  };

  const renderAssetHeader = () => {
    const assetPosition =
      activeAssetIndex >= 0 ? activeAssetIndex + 1 : rows.length ? 1 : 0;

    return (
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={2}
          sx={{ mb: 1.25 }}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <IconButtonWithTooltip
              icon={KeyboardArrowLeftIcon}
              tooltipTitle='Previous asset (Alt+Left)'
              onClick={() => onNavigateAsset(-1)}
              color='primary'
              size='small'
              disabled={activeAssetIndex <= 0}
              sx={{ width: 32, height: 32 }}
            />
            <Typography variant='caption' sx={{ fontWeight: 800 }}>
              Asset {assetPosition} of {rows.length}
            </Typography>
            <IconButtonWithTooltip
              icon={KeyboardArrowRightIcon}
              tooltipTitle='Next asset (Alt+Right)'
              onClick={() => onNavigateAsset(1)}
              color='primary'
              size='small'
              disabled={
                activeAssetIndex < 0 || activeAssetIndex >= rows.length - 1
              }
              sx={{ width: 32, height: 32 }}
            />
          </Stack>

          <Stack direction='row' alignItems='center' spacing={0.5}>
            <IconButtonWithTooltip
              icon={ContentCopyIcon}
              tooltipTitle='Duplicate asset'
              onClick={onDuplicateAsset}
              color='primary'
              size='small'
              disabled={!activeAsset || !canEditSchedule}
            />
            <IconButtonWithTooltip
              icon={DeleteOutlineIcon}
              tooltipTitle='Delete asset'
              onClick={() => activeAsset && onDeleteAsset(activeAsset.rowKey)}
              color='error'
              size='small'
              disabled={!activeAsset || !canEditSchedule}
            />
            <IconButtonWithTooltip
              icon={CloseIcon}
              tooltipTitle='Close'
              onClick={onClose}
              color='primary'
              size='small'
            />
          </Stack>
        </Stack>

        {activeAsset ? (
          <Stack spacing={1}>
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              flexWrap='wrap'
            >
              <Typography
                variant='h6'
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 800,
                  lineHeight: 1.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {activeAsset.assetDescription || "New Asset"}
              </Typography>
              {activeAsset.disposal && (
                <Tooltip title='This asset will be flowed to the Disposal Schedule in the current YA'>
                  <Chip
                    label='Disposed'
                    size='small'
                    color='warning'
                    variant='outlined'
                    sx={{ borderRadius: 1, fontWeight: 700 }}
                  />
                </Tooltip>
              )}
              {activeAsset.deferred && (
                <Chip
                  label='Deferred'
                  size='small'
                  color='info'
                  variant='outlined'
                  sx={{ borderRadius: 1, fontWeight: 700 }}
                />
              )}
              {isPLScheduleAsset(activeAsset) && (
                <Tooltip title='This asset is expensed in P&L and will only flow to the capital allowance schedules'>
                  <Chip
                    label='Claim CA only'
                    size='small'
                    color='primary'
                    variant='outlined'
                    sx={{ borderRadius: 1, fontWeight: 700 }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Stack>
        ) : (
          <Typography color='text.secondary'>
            Select an asset to edit.
          </Typography>
        )}
      </Box>
    );
  };

  const renderDepreciationFlow = (row) => {
    if (!isApplicableType(row.caType)) return null;

    const isDisposed = row.disposal;
    const depreciationRate = row.costOfAsset
      ? ((row.costOfAsset - row.tvdCf) / row.costOfAsset) * 100
      : 0;

    const flowGroups = [
      {
        label: "Opening",
        items: [{ field: "twdvBf", label: "TWDV B/F", type: "input" }],
      },
      {
        label: "Current Year",
        items: [
          { field: "ia", label: "IA", type: "calculated" },
          { field: "aa", label: "AA", type: "calculated" },
        ],
      },
      {
        label: "Closing",
        items: [{ field: "twdvCf", label: "TWDV C/F", type: "result" }],
      },
    ];

    const getCardStyles = (type) => {
      const base = {
        flex: 1,
        minWidth: 0,
        p: 1.25,
        borderRadius: 1.5,
        border: "1.5px solid",
        transition: "all 0.2s ease",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        },
      };

      switch (type) {
        case "input":
          return {
            ...base,
            borderColor: "divider",
            bgcolor: alpha(theme.palette.action.hover, 0.15),
          };
        case "calculated":
          return {
            ...base,
            borderColor: alpha(theme.palette.info.main, 0.3),
            bgcolor: alpha(theme.palette.info.main, 0.05),
          };
        case "result":
          return {
            ...base,
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          };
        default:
          return base;
      }
    };

    return (
      <>
        {!isDisposed ? (
          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.95),
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
            >
              {flowGroups.map((group, groupIdx) => (
                <React.Fragment key={group.label}>
                  <Box
                    sx={{ flex: group.items.length, display: "flex", gap: 1 }}
                  >
                    {group.items.map((item) => {
                      const value = displayValue(row, item.field);
                      const isDisposedValue = value === "Disposed";

                      return (
                        <Tooltip
                          key={item.field}
                          title={
                            <Box>
                              <Typography variant='caption' fontWeight={700}>
                                {FIELD_LABELS[item.field]}
                              </Typography>
                              <Typography variant='caption' display='block'>
                                {item.type === "input"
                                  ? "Manual input"
                                  : item.type === "calculated"
                                    ? "Auto-calculated"
                                    : "Final position"}
                              </Typography>
                            </Box>
                          }
                          arrow
                        >
                          <Box sx={getCardStyles(item.type)}>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              variant='body2'
                              sx={{
                                fontWeight: 900,
                                textAlign: "right",
                                fontSize: "0.95rem",
                                color: isDisposedValue
                                  ? "warning.main"
                                  : item.type === "result"
                                    ? "primary.main"
                                    : "text.primary",
                              }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>

                  {groupIdx < flowGroups.length - 1 && (
                    <Stack
                      direction={{ xs: "row", sm: "column" }}
                      alignItems='center'
                      spacing={0.5}
                      sx={{
                        px: { xs: 0, sm: 0.5 },
                        py: { xs: 0.5, sm: 0 },
                      }}
                    >
                      <KeyboardArrowRightIcon
                        sx={{
                          display: { xs: "none", sm: "block" },
                          color: "text.disabled",
                          fontSize: 20,
                        }}
                      />
                      <KeyboardArrowDownIcon
                        sx={{
                          display: { xs: "block", sm: "none" },
                          color: "text.disabled",
                          fontSize: 20,
                        }}
                      />
                    </Stack>
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Paper>
        ) : (
          <Alert severity='warning' icon={<Inventory2OutlinedIcon />}>
            <Typography variant='caption' fontWeight={600}>
              Asset disposed
            </Typography>
          </Alert>
        )}
      </>
    );
  };

  const drawerGroups = headerGroups
    .map((group) => ({
      ...group,
      fields: group.fields.filter(
        (field) =>
          (visibleFields.includes(field) ||
            conditionalDrawerFields.has(field)) &&
          (!activeAsset || isDrawerFieldVisible(activeAsset, field))
      ),
    }))
    .filter((group) => group.fields.length > 0);

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560, md: 640 },
          bgcolor: "background.paper",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {renderAssetHeader()}

        {activeAsset ? (
          <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2.5 }}>
            <Stack spacing={2.5}>
              {renderDepreciationFlow(activeAsset)}

              {drawerGroups.map((group) => (
                <>
                  {group.label === "Current YA & Closing" ? (
                    <Accordion
                      key={group.label}
                      disableGutters
                      elevation={0}
                      expanded={expandedGroups.has(group.label)}
                      onChange={() => toggleDrawerGroup(group.label)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "8px !important",
                        overflow: "hidden",
                        "&::before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize='small' />}
                        sx={{
                          minHeight: 46,
                          px: 2,
                          "& .MuiAccordionSummary-content": {
                            my: 1,
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          },
                        }}
                      >
                        <Typography
                          variant='subtitle2'
                          sx={{ fontWeight: 800 }}
                        >
                          {group.label}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                        {renderDrawerSectionFields(activeAsset, group.fields)}
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    renderDrawerSectionFields(activeAsset, group.fields)
                  )}
                </>
              ))}
            </Stack>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography color='text.secondary'>
              Select an asset to edit.
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default FaRegisterDrawer;
